import React from "react";
import { Menu } from "@design-system/Menu/Menu.lib";
import { FinancialAccount } from "@common/types/financialAccount";
import { FinancialConnection } from "@common/types/financialConnection";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { getNonFixablePlaidConnectionError } from "@common/helpers/financialConnection";
import { UnsupportedPlaidConnectionTooltipContent } from "@components/plaid/UnsupportedPlaidConnection";
import { financialAccountIsErrored } from "@helpers/financialAccount";
import { useConnectionProviderLabel } from "@hooks/useConnectionProviderLabel";
import {
    useAccountDisablingMutation,
    useAccountEnablingMutation,
} from "@mutations/financialAccount";

interface FinancialAccountStateManagerProps {
    account: FinancialAccount;
    connection: FinancialConnection | IntegrationConnection;
}

export const FinancialAccountStateManager: React.FC<
    FinancialAccountStateManagerProps
> = ({ account, connection }) => {
    const enableAccount = useAccountEnablingMutation(account);
    const disableAccount = useAccountDisablingMutation(account);
    const providerLabel = useConnectionProviderLabel(connection);
    const nonFixablePlaidError = getNonFixablePlaidConnectionError(connection);
    const mustFixBeforeEnabling =
        account.disabled && financialAccountIsErrored(account);

    const getDisabledReason = () => {
        if (nonFixablePlaidError) {
            return (
                <UnsupportedPlaidConnectionTooltipContent
                    error={nonFixablePlaidError}
                />
            );
        }
        if (mustFixBeforeEnabling) {
            return "Fix the connection before enabling this account.";
        }
        return undefined;
    };

    const getTooltip = () => {
        if (account.disabled) {
            return undefined;
        }
        if (connection.error) {
            return "This account is disconnected. Use Fix connection on the row to reconnect.";
        }
        return (
            <>
                Disconnecting {providerLabel} won&apos;t bring your new
                transactions into the platform. You can choose if you want to
                keep older transactions or delete them.
            </>
        );
    };

    return (
        <Menu.ToggleItem
            checked={account.disabled}
            onCheckedChange={(isDisabled) => {
                if (isDisabled) {
                    disableAccount.mutate();
                } else {
                    enableAccount.mutate();
                }
            }}
            disabled={
                disableAccount.isPending ||
                enableAccount.isPending ||
                nonFixablePlaidError != null ||
                mustFixBeforeEnabling
            }
            disabledReason={getDisabledReason()}
            tooltip={getTooltip()}
        >
            Disable account
        </Menu.ToggleItem>
    );
};
